import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { format, isSameMonth } from "date-fns";
import { CalendarDayCell } from "./CalendarDayCell";
import { CalendarLegend } from "./CalendarLegend";
import { PTOTableView } from "./PTOTableView";

export function PTOCalendarView({
  currentMonth,
  monthMatrix,
  dayEvents,
  handlePrevMonth,
  handleNextMonth,
  onRequestClick,
  currentWeekStart,
  weekDays,
  handlePrevWeek,
  handleNextWeek,
  mode = "month",
  onModeChange,
  onJumpToToday,
  plantFilter = "0",
  statusFilter = "",
  highlightedRequestId,
  onHighlightRequest,
  requests = [],
  isLoading = false,
  approveMutation,
  handleReject,
  cancelMutation,
  userType,
  searchVal = "",
  onRowDoubleClickDate,
}: {
  currentMonth: Date;
  monthMatrix: Date[][];
  dayEvents: Map<string, any[]>;
  handlePrevMonth: () => void;
  handleNextMonth: () => void;
  onRequestClick: (request: any) => void;
  currentWeekStart: Date;
  weekDays: Date[];
  handlePrevWeek: () => void;
  handleNextWeek: () => void;
  mode?: string;
  onModeChange?: (mode: string) => void;
  onJumpToToday?: () => void;
  plantFilter?: string;
  statusFilter?: string;
  highlightedRequestId?: number | null;
  requests?: any[];
  isLoading?: boolean;
  approveMutation?: any;
  cancelMutation?: any;
  handleReject?: (id: number) => void;
  searchVal?: string;
  userType?: string;
  onRowDoubleClickDate?: (startDate: string, requestId?: number) => void;
  onHighlightRequest?: (requestId: number | null) => void;
}) {
  const [moreForDay, setMoreForDay] = useState<string | null>(null);
  const [scrollTargetDate, setScrollTargetDate] = useState<string | null>(null);
  const [showPendingTable, setShowPendingTable] = useState(false);
  const weekGridRef = useRef<HTMLDivElement>(null);

  const pendingRequests = useMemo(
    () =>
      (requests || []).filter(
        (request: any) =>
          String(request?.status || "").toLowerCase() === "pending"
      ),
    [requests]
  );

  const isWeek = mode === "week";
  const headerLabel = isWeek
    ? `${format(currentWeekStart, "MMM d")} - ${format(
        new Date(
          currentWeekStart.getFullYear(),
          currentWeekStart.getMonth(),
          currentWeekStart.getDate() + 6
        ),
        "MMM d, yyyy"
      )}`
    : format(currentMonth, "MMMM yyyy");

  const handlePrev = isWeek ? handlePrevWeek : handlePrevMonth;
  const handleNext = isWeek ? handleNextWeek : handleNextMonth;

  useEffect(() => {
    if (!scrollTargetDate || !weekGridRef.current) return;
    const id = setTimeout(() => {
      const el = weekGridRef.current!.querySelector(
        `[data-date="${scrollTargetDate}"]`
      );
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
      setScrollTargetDate(null);
    }, 0);
    return () => clearTimeout(id);
  }, [scrollTargetDate, weekDays]);
  return (
    <div className="tw-flex tw-flex-col xl:tw-flex-row tw-gap-3">
      <div className="tw-flex-1 tw-bg-white dark:tw-bg-[#0D1117] tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-p-2 sm:tw-p-4">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-1 sm:tw-gap-2 tw-mb-3 tw-flex-wrap">
          <div className="tw-flex tw-items-center tw-gap-1 sm:tw-gap-2">
            <button
              onClick={handlePrev}
              className="tw-p-1 sm:tw-p-2 tw-border tw-rounded-md"
              aria-label={isWeek ? "Previous week" : "Previous month"}
            >
              <ChevronLeft className="tw-w-3 tw-h-3 sm:tw-w-4 sm:tw-h-4" />
            </button>
            <div className="tw-font-medium tw-text-xs sm:tw-text-base tw-min-w-28 tw-text-center">
              {headerLabel}
            </div>
            <button
              onClick={handleNext}
              className="tw-p-1 sm:tw-p-2 tw-border tw-rounded-md"
              aria-label={isWeek ? "Next week" : "Next month"}
            >
              <ChevronRight className="tw-w-3 tw-h-3 sm:tw-w-4 sm:tw-h-4" />
            </button>
            <button
              onClick={() => {
                onJumpToToday?.();
                const todayStr = format(new Date(), "yyyy-MM-dd");
                setScrollTargetDate(todayStr);
              }}
              className="tw-ml-1 sm:tw-ml-2 tw-px-1.5 sm:tw-px-2 tw-py-0.5 sm:tw-py-1 tw-border tw-rounded-md tw-text-[10px] sm:tw-text-sm hover:tw-bg-gray-50 dark:tw-hover:bg-[#0A1E2D]"
              aria-label="Jump to today"
            >
              Today
            </button>
          </div>
          <div className="tw-flex tw-items-center tw-gap-1 sm:tw-gap-2">
            <button
              className={`tw-px-2 sm:tw-px-3 tw-py-1 sm:tw-py-1.5 tw-rounded-md tw-text-[10px] sm:tw-text-sm tw-border ${
                showPendingTable
                  ? "tw-bg-blue-600 tw-text-white tw-border-blue-600"
                  : "tw-bg-white tw-text-gray-700 tw-border-gray-300"
              }`}
              onClick={() => setShowPendingTable((prev) => !prev)}
            >
              Pending
            </button>
            <button
              className={`tw-px-2 sm:tw-px-3 tw-py-1 sm:tw-py-1.5 tw-rounded-md tw-text-[10px] sm:tw-text-sm tw-border ${
                !isWeek
                  ? "tw-bg-blue-600 tw-text-white tw-border-blue-600"
                  : "tw-bg-white tw-text-gray-700 tw-border-gray-300"
              }`}
              onClick={() => onModeChange?.("month")}
            >
              Month
            </button>
            <button
              className={`tw-px-2 sm:tw-px-3 tw-py-1 sm:tw-py-1.5 tw-rounded-md tw-text-[10px] sm:tw-text-sm tw-border ${
                isWeek
                  ? "tw-bg-blue-600 tw-text-white tw-border-blue-600"
                  : "tw-bg-white tw-text-gray-700 tw-border-gray-300"
              }`}
              onClick={() => onModeChange?.("week")}
            >
              Week
            </button>
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-7 tw-text-center tw-text-xs tw-text-gray-600 dark:tw-text-gray-300">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="tw-py-1">
              {d}
            </div>
          ))}
        </div>

        {isWeek ? (
          <div
            ref={weekGridRef}
            className="tw-grid tw-grid-cols-7 tw-gap-1"
            onClick={() => {
              if (moreForDay) setMoreForDay(null);
            }}
          >
            {weekDays.map((day, di) => {
              const dateStr = format(day, "yyyy-MM-dd");
              const inMonth = true;
              const dayList = dayEvents.get(dateStr) || [];
              return (
                <CalendarDayCell
                  key={di}
                  day={day}
                  dateStr={dateStr}
                  inMonth={inMonth}
                  dayList={dayList}
                  onRequestClick={onRequestClick}
                  onShowMore={() => setMoreForDay(dateStr)}
                  moreForDay={moreForDay}
                  onCloseMore={() => setMoreForDay(null)}
                  showAll={true}
                  minHeightClass="tw-min-h-[420px]"
                  plantFilter={plantFilter}
                  statusFilter={statusFilter}
                  highlightedRequestId={highlightedRequestId}
                />
              );
            })}
          </div>
        ) : (
          <div
            className="tw-grid tw-grid-cols-7 tw-gap-1"
            onClick={() => {
              if (moreForDay) setMoreForDay(null);
            }}
          >
            {monthMatrix.map((week, wi) => (
              <div className="tw-contents" key={wi}>
                {week.map((day, di) => {
                  const dateStr = format(day, "yyyy-MM-dd");
                  const inMonth = isSameMonth(day, currentMonth);
                  const dayList = dayEvents.get(dateStr) || [];

                  return (
                    <CalendarDayCell
                      key={di}
                      day={day}
                      dateStr={dateStr}
                      inMonth={inMonth}
                      dayList={dayList}
                      onRequestClick={onRequestClick}
                      onShowMore={() => setMoreForDay(dateStr)}
                      moreForDay={moreForDay}
                      onCloseMore={() => setMoreForDay(null)}
                      plantFilter={plantFilter}
                      statusFilter={statusFilter}
                      highlightedRequestId={highlightedRequestId}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        )}

        <CalendarLegend plantFilter={plantFilter} statusFilter={statusFilter} />
      </div>

      {showPendingTable && (
        <div
          className="tw-w-full xl:tw-w-[28vw]"
          style={{ minWidth: "320px", maxWidth: "32vw" }}
        >
          <PTOTableView
            requests={pendingRequests}
            isLoading={isLoading}
            approveMutation={approveMutation}
            handleReject={handleReject || (() => {})}
            cancelMutation={cancelMutation}
            searchVal={searchVal}
            userType={userType}
            onRowClickDate={(startDate, requestId) => {
              onRowDoubleClickDate?.(startDate, requestId);
              onHighlightRequest?.(requestId ?? null);
            }}
            showCancelAction={false}
            highlightedRequestId={highlightedRequestId}
          />
        </div>
      )}
    </div>
  );
}
